import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { getStrandMoldHistory } from "../../repositories/CasterMoldRepository";
import {
  transformStrandMoldHistory,
  type StrandMoldHistoryEntry,
} from "../../models";
import type { PartDetailsHistoryAPI } from "../../models";
/**
 * TanStack Query hook for strand mold install/uninstall history.
 *
 * Calls EQPGetPartDetails in location mode (ParentPartId=strandId, PositionId=1)
 * without the @AssemblyPartTypeId filter, so mold assemblies are included.
 *
 * The transform filters to only show events relevant to THIS strand.
 *
 * Query key: ["caster-mold-strand-history", strandId]
 */
export const useStrandMoldHistoryQuery = (strandId: number | null) => {
  const query = useQuery({
    queryKey: ["caster-mold-strand-history", strandId!] as const,
    queryFn: async (): Promise<PartDetailsHistoryAPI[]> => {
      if (strandId === null) return [];
      const resp = await getStrandMoldHistory(strandId);
      if (!resp.ok) return [];
      return get(resp, "data.tables[2].rows", []);
    },
    enabled: strandId !== null,
    refetchOnMount: "always" as const,
  });

  const entries: StrandMoldHistoryEntry[] = useMemo(() => {
    if (!query.data || strandId === null) return [];
    return transformStrandMoldHistory(query.data, strandId);
  }, [query.data, strandId]);

  return {
    entries,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
};
